/**
 * Enquiry and contact submissions — callable Cloud Functions.
 *
 * These are the only two writes a visitor can make. They deliberately do NOT
 * write to Firestore from the browser: the function validates every field,
 * applies the spam gate and per-IP rate limit, allocates a sequential reference
 * and sends both the team notification and the customer acknowledgement.
 *
 * The UI contract is unchanged from the original mock service — the pages'
 * existing loading / success / error states work as they always did.
 */

import { httpsCallable } from "firebase/functions";
import { functions, isFirebaseConfigured } from "@/lib/firebase";

/** Turn a Firebase callable error into something worth showing a human. */
const friendlyError = (error) => {
  const code = error?.code || "";

  if (code.includes("resource-exhausted")) {
    return new Error(
      error.message ||
        "Too many submissions from this connection. Please try again in a few minutes."
    );
  }
  if (code.includes("invalid-argument")) {
    return new Error(error.message || "Please check the form and try again.");
  }
  if (code.includes("unavailable") || code.includes("internal")) {
    return new Error(
      "We could not reach our servers. Please check your connection and try again, or contact us directly."
    );
  }
  return new Error(
    error?.message || "Something went wrong sending your enquiry. Please try again."
  );
};

const call = async (name, payload) => {
  if (!isFirebaseConfigured || !functions) {
    throw new Error(
      "The enquiry service is not configured for this build. Please contact us directly."
    );
  }
  try {
    const result = await httpsCallable(functions, name)(payload);
    return result.data;
  } catch (error) {
    throw friendlyError(error);
  }
};

export const enquiriesService = {
  /**
   * payload: { name, email, phone, company, message, agree, items: [cartItem],
   *            website?, elapsedMs? }
   * `website` is the honeypot field and `elapsedMs` how long the form was open;
   * both are read by the server's spam gate and are optional.
   */
  submitInquiry: (payload) => call("submitEnquiry", payload),

  /** payload: { name, email, phone, company, subject, message, agree, website?, elapsedMs? } */
  submitContactForm: (payload) => call("submitContactMessage", payload),
};

export default enquiriesService;
